import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Input, Select, DatePicker, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { LeftOutlined, EyeOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import type { MatGapIssueRequest } from '../../../types';
import { useGetMatGapIssueRequests } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  'pending',
  'approved',
  'partially_issued',
  'issued',
  'rejected',
  'cancelled',
];

export default function MatGapIssueRequestsGeneral() {
  const navigate = useNavigate();

  const { data: hdrData, isLoading, refetch } = useGetMatGapIssueRequests() as any;
  const data: MatGapIssueRequest[] = useMemo(() => (Array.isArray(hdrData) ? hdrData : (hdrData?.data ?? [])), [hdrData]);

  // If returning from edit, force refresh once
  useEffect(() => {
    try {
      const key = 'refresh_mat_issue_requests';
      if (sessionStorage.getItem(key) === '1') {
        refetch?.();
        sessionStorage.removeItem(key);
      }
    } catch {}
  }, [refetch]);

  // Filters
  const [refNo, setRefNo] = useState<string>('');
  const [requester, setRequester] = useState<string>(''); // code string
  const [requesterName, setRequesterName] = useState<string>(''); // human name
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // data is provided by hook with caching; no manual load needed

  // Helper to normalize strings
  const norm = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();

  // Base filter for building cascading options (date + status only)
  const baseForOptions = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    return (data || []).filter((r) => {
      if (status && r.status !== status) return false;
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [data, status, dateRange]);

  // Distinct value options for combo filters (cascading)
  const refNoOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !requester || String(r.requester) === requester)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .forEach(r => { if (r?.ref_no) s.add(String(r.ref_no)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, requester, requesterName]);

  const requesterOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requesterName || norm(r.requester_name).includes(norm(requesterName)))
      .forEach(r => { if (r?.requester) s.add(String(r.requester)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requesterName]);

  const requesterNameOptions = useMemo(() => {
    const s = new Set<string>();
    baseForOptions
      .filter(r => !refNo || String(r.ref_no) === refNo)
      .filter(r => !requester || String(r.requester) === requester)
      .forEach(r => { if (r?.requester_name) s.add(String(r.requester_name)); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [baseForOptions, refNo, requester]);

  // Auto-clear invalid selections when cascading reduces available options
  useEffect(() => {
    if (refNo && !refNoOptions.some(o => o.value === refNo)) setRefNo('');
  }, [refNoOptions, refNo]);
  useEffect(() => {
    if (requester && !requesterOptions.some(o => o.value === requester)) setRequester('');
  }, [requesterOptions, requester]);
  useEffect(() => {
    if (requesterName && !requesterNameOptions.some(o => o.value === requesterName)) setRequesterName('');
  }, [requesterNameOptions, requesterName]);

  const filtered = useMemo(() => {
    const [start, end] = dateRange || [null, null];

    return (data || []).filter((r) => {
      if (refNo && String(r.ref_no) !== refNo) return false;
      if (requester && String(r.requester) !== requester) return false;
      if (requesterName && !norm(r.requester_name).includes(norm(requesterName))) return false;
      if (status && r.status !== status) return false;
      if (start && r.req_date && dayjs(r.req_date).isBefore(start, 'day')) return false;
      if (end && r.req_date && dayjs(r.req_date).isAfter(end, 'day')) return false;
      return true;
    });
  }, [data, refNo, requester, requesterName, status, dateRange]);

  const columns: any[] = useMemo(() => [
    { title: 'Ref No', dataIndex: 'ref_no', key: 'ref_no', width: 160, sorter: (a: MatGapIssueRequest, b: MatGapIssueRequest) => String(a.ref_no).localeCompare(String(b.ref_no)) },
    { title: 'Requester (Code)', dataIndex: 'requester', key: 'requester', width: 160 },
    { title: 'Requester Name', dataIndex: 'requester_name', key: 'requester_name', width: 220 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, render: (v: string) => <Tag color={
      v === 'pending' ? 'gold' :
      v === 'approved' ? 'blue' :
      v === 'partially_issued' ? 'purple' :
      v === 'issued' ? 'green' :
      v === 'rejected' ? 'red' :
      v === 'cancelled' ? 'default' : 'default'
    }>{v}</Tag>, exportValue: (r: MatGapIssueRequest) => r.status },
    { title: 'Request Date', dataIndex: 'req_date', key: 'req_date', width: 140,
      sorter: (a: MatGapIssueRequest, b: MatGapIssueRequest) => dayjs(a.req_date || 0).unix() - dayjs(b.req_date || 0).unix(),
      render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '',
      exportValue: (r: MatGapIssueRequest) => (r.req_date ? dayjs(r.req_date).format('YYYY-MM-DD') : '')
    },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 120,
      render: (_: any, row: MatGapIssueRequest) => (
        <Space>
          <Button
            action="read"
            size="small"
            type="text"
            icon={<EyeOutlined />}
            aria-label="View"
            onClick={() => navigate(`/material/gap-material/issue-request/view/${row.id}`, { state: { ref_no: row.ref_no } })}
          />
          <Button
            action="edit"
            size="small"
            type="primary"
            icon={<EditOutlined />}
            aria-label="Edit"
            onClick={() => navigate(`/material/gap-material/issue-request/edit/${row.id}`, { state: { ref_no: row.ref_no } })}
          />
        </Space>
      )
    }
  ], [navigate]);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Issue Requests</Title>
              <Text type="secondary">Browse and manage material issue requests</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select
              showSearch
              allowClear
              placeholder="Ref No"
              value={refNo || undefined}
              onChange={(v) => setRefNo(v || '')}
              options={refNoOptions}
              style={{ width: 150 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              showSearch
              allowClear
              placeholder="Requester (Code)"
              value={requester || undefined}
              onChange={(v) => setRequester(v || '')}
              options={requesterOptions}
              style={{ width: 150 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              showSearch
              allowClear
              placeholder="Requester Name"
              value={requesterName || undefined}
              onChange={(v) => setRequesterName(v || '')}
              options={requesterNameOptions}
              style={{ width: 180 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Status"
              allowClear
              value={status}
              onChange={(v) => setStatus(v)}
              style={{ width: 140 }}
              options={STATUS_OPTIONS.map(s => ({ value: s, label: s }))}
            />
            <RangePicker size="small" value={dateRange as any} onChange={(v) => setDateRange(v as any)} style={{ width: 240 }} />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch?.()}>Refresh</Button>
            <Button size="small" onClick={() => { setRefNo(''); setRequester(''); setRequesterName(''); setStatus(undefined); setDateRange(null); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <StandardTable<MatGapIssueRequest>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
